/**
 * AdjunctionExplainer — progressive-disclosure explainer for adjunctions.
 *
 * An adjunction `F ⊣ G` is the universal vehicle for every Dual Pair
 * Guarantee and every Structural Duality Principle in CSK. This explainer
 * is the formal anchor that the SSM-side INV.CONSISTENCY and ALWAYS
 * explainers — and every sibling adjunction explainer (triangle
 * identities, adjunction → monad/comonad, Beck-Chevalley failure, higher
 * adjunctions) — link to and from.
 *
 * The card opens at a no-notation TL;DR, then climbs through Intuition,
 * Formal, CSK relevance, and References. Two diagrams ride in the diagram
 * slot: the canonical adjunction triangle (with hover-highlighted unit
 * and counit) and the triangle-identity picture (with per-leg hover) so
 * curious readers see the whole skeleton before any prose.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useState, useCallback } from "react";
import { COMPOSITIONALITY_PRINCIPLE_COLOR, FONT, GRAY } from "../../design-system/tokens.js";
import { MathBlock, MathInline, tex } from "../../components/math/index.js";
import {
  AdjunctionDiagram,
  ExplainerCard,
  TriangleIdentityDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
  type TriangleIdentityLeg,
} from "../shared/index.js";

const ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.ALWAYS.color;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-adjunction",
  kind: "Categorical structure",
  title: "Adjunction",
  subtitle: "F ⊣ G : C ⇄ D — the universal shape of a Dual Pair",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Triangle identities", href: "#explainer-triangle-identities", accent: ACCENT },
  { label: "Adjunctions → monads", href: "#explainer-adjunctions-generate-monads", accent: ACCENT },
  { label: "Beck-Chevalley", href: "#explainer-beck-chevalley", accent: ACCENT },
  { label: "Higher / graded adjunctions", href: "#explainer-higher-adjunctions", accent: ACCENT },
  { label: "INV.CONSISTENCY (DPG)", href: "#explainer-inv-consistency" },
  { label: "ALWAYS (SDP)", href: "#explainer-always", accent: ACCENT },
  { label: "F-Algebra", href: "#explainer-falgebra" },
  { label: "F-Coalgebra", href: "#explainer-fcoalgebra" },
];

// inline: local — flat (data-driven) legend rows so React reuses the
// same <button> nodes across highlight transitions; a nested component
// type would be re-created on every render and drop focus mid-keystroke.
interface AdjunctionLegendRow {
  readonly leg: TriangleIdentityLeg;
  readonly prose: string;
}

const ADJUNCTION_LEGEND_ROWS: readonly AdjunctionLegendRow[] = [
  { leg: "Fη", prose: "Insert the value F builds, then F-collapse via the counit." },
  { leg: "εF", prose: "On the F side, the counit acts on the F-image and lands back at F." },
  { leg: "ηG", prose: "Insert the value G observes, then G-collapse via the counit." },
  { leg: "Gε", prose: "On the G side, the counit is observed through G and lands back at G." },
];

function TriangleIdentityHoverLegend(): React.JSX.Element {
  const [highlighted, setHighlighted] = useState<TriangleIdentityLeg | undefined>(undefined);
  const setLeg = useCallback(
    (leg: TriangleIdentityLeg | undefined): (() => void) =>
      () => {
        setHighlighted(leg);
      },
    [],
  );
  return (
    <div style={{ display: "grid", gap: "0.5rem" }}>
      <TriangleIdentityDiagram accent={ACCENT} highlighted={highlighted} />
      <div
        role="group"
        aria-label="Hover any triangle leg to highlight it on the diagram"
        style={{
          display: "grid",
          gap: "0.35rem",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        }}
      >
        {ADJUNCTION_LEGEND_ROWS.map((row) => (
          <button
            key={row.leg}
            type="button"
            onMouseEnter={setLeg(row.leg)}
            onFocus={setLeg(row.leg)}
            onMouseLeave={setLeg(undefined)}
            onBlur={setLeg(undefined)}
            aria-pressed={highlighted === row.leg}
            style={{
              appearance: "none",
              border: highlighted === row.leg ? `2px solid ${ACCENT}` : `1px solid ${GRAY[300]}`,
              background: highlighted === row.leg ? `${ACCENT}1A` : "white",
              borderRadius: "0.4rem",
              padding: "0.3rem 0.55rem",
              textAlign: "left",
              cursor: "pointer",
              fontSize: "0.75rem",
              color: GRAY[700],
              fontFamily: FONT.body,
            }}
          >
            <span style={{ fontFamily: FONT.mono, fontWeight: 700, color: ACCENT }}>{row.leg}</span>
            <span style={{ marginLeft: "0.4rem" }}>{row.prose}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      An <strong>adjunction</strong> is the categorical statement that two operations are mirror
      images of each other in a precise, computable way: one <em>builds</em> something, the other{" "}
      <em>observes</em> it, and the two are paired by a universal trade — picking a morphism on one
      side is exactly the same data as picking a morphism on the other. Every CSK Dual Pair
      Guarantee and every Structural Duality Principle is an adjunction; security follows from the
      fact that the trade is a structural law, not a runtime convention.
    </p>
  ),
  intuition: (
    <>
      <p>
        Think of three everyday pairs. Each is an adjunction, and each tells the same story from a
        different angle:
      </p>
      <ul>
        <li>
          <strong>Free / forgetful.</strong> Take a set of letters; build the free group on it
          (every formal product, mod the group laws); then forget that you have a group and look at
          the underlying set. The original letters are still there, sitting inside the bigger set
          via the <em>unit</em> <MathInline>{tex`\eta`}</MathInline>. The free construction is "the
          smallest group that handles your letters as if they already had multiplication"; the
          forgetful functor is "ignore the multiplication." They are a perfect duality.
        </li>
        <li>
          <strong>Currying / uncurrying.</strong> A function{" "}
          <MathInline>{tex`(A \times B) \to C`}</MathInline> carries exactly the same data as a
          function <MathInline>{tex`A \to (B \to C)`}</MathInline>. Currying takes you one way,
          uncurrying takes you back; their composition in either order is the identity. That is an
          adjunction between <MathInline>{tex`(- \times B)`}</MathInline> and{" "}
          <MathInline>{tex`(B \to -)`}</MathInline>.
        </li>
        <li>
          <strong>Image / preimage.</strong> Given a function{" "}
          <MathInline>{tex`f : X \to Y`}</MathInline>, the image{" "}
          <MathInline>{tex`f_{!}`}</MathInline> pushes subsets of <MathInline>{tex`X`}</MathInline>{" "}
          forward; the preimage <MathInline>{tex`f^{*}`}</MathInline> pulls subsets of{" "}
          <MathInline>{tex`Y`}</MathInline> back. Image is left-adjoint to preimage — and the
          failure of this adjunction to commute with substitution is the very phenomenon CSK calls a{" "}
          <em>seam</em>.
        </li>
      </ul>
      <p>
        Every CSK dual pair has this shape: one direction builds, the other observes; the{" "}
        <em>unit</em> certifies that what you built can be observed, and the <em>counit</em>{" "}
        certifies that what you observe can be built. The two <em>triangle identities</em> say these
        two certifications fit together — going "build, observe, then collapse" or "observe, build,
        then collapse" returns you to where you started.
      </p>
      <p>
        Why does this matter for safety? Because if the trade really is two-sided, then a commitment
        cannot be observed without first being built and a budget cannot be released without first
        being consumed. The adjunction is the algebraic record that "build" and "observe" cannot
        escape each other.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        An <strong>adjunction</strong> between categories <MathInline>{tex`C`}</MathInline> and{" "}
        <MathInline>{tex`D`}</MathInline> is a pair of functors{" "}
        <MathInline>{tex`F : C \to D`}</MathInline> and <MathInline>{tex`G : D \to C`}</MathInline>{" "}
        together with two natural transformations:
      </p>
      <ul>
        <li>
          <strong>Unit</strong>{" "}
          <MathInline>{tex`\eta : \mathrm{Id}_{C} \Rightarrow G \circ F`}</MathInline> — for every
          object <MathInline>{tex`A`}</MathInline> in <MathInline>{tex`C`}</MathInline>, a map{" "}
          <MathInline>{tex`\eta_{A} : A \to G(F(A))`}</MathInline>.
        </li>
        <li>
          <strong>Counit</strong>{" "}
          <MathInline>{tex`\varepsilon : F \circ G \Rightarrow \mathrm{Id}_{D}`}</MathInline> — for
          every object <MathInline>{tex`B`}</MathInline> in <MathInline>{tex`D`}</MathInline>, a map{" "}
          <MathInline>{tex`\varepsilon_{B} : F(G(B)) \to B`}</MathInline>.
        </li>
      </ul>
      <p>
        satisfying the two <strong>triangle identities</strong>:
      </p>
      <MathBlock ariaLabel="Triangle identities: (εF) ∘ (Fη) = id_F and (Gε) ∘ (ηG) = id_G">
        {tex`(\varepsilon F) \circ (F \eta) \;=\; \mathrm{id}_{F}
\qquad
(G \varepsilon) \circ (\eta G) \;=\; \mathrm{id}_{G}`}
      </MathBlock>
      <TriangleIdentityHoverLegend />
      <p style={{ marginTop: "0.75rem" }}>
        Equivalently — and this is the workhorse formulation — an adjunction is a natural
        isomorphism
      </p>
      <MathBlock ariaLabel="Hom_D(F A, B) is naturally isomorphic to Hom_C(A, G B)">
        {tex`\mathrm{Hom}_{D}(F A,\, B) \;\cong\; \mathrm{Hom}_{C}(A,\, G B)`}
      </MathBlock>
      <p>
        Picking a morphism out of <MathInline>{tex`F A`}</MathInline> in{" "}
        <MathInline>{tex`D`}</MathInline> is the <em>same data</em> as picking a morphism into{" "}
        <MathInline>{tex`G B`}</MathInline> in <MathInline>{tex`C`}</MathInline>. The unit and
        counit are the two images of the identity under this bijection.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        <strong>Concrete bijection trace.</strong> Starting from a morphism{" "}
        <MathInline>{tex`f : F A \to B`}</MathInline> in <MathInline>{tex`D`}</MathInline>, the{" "}
        <em>transpose</em> <MathInline>{tex`\tilde{f} : A \to G B`}</MathInline> in{" "}
        <MathInline>{tex`C`}</MathInline> is the composite{" "}
        <MathInline ariaLabel="A maps via η_A to G(F(A)) and then via G(f) to G(B)">
          {tex`A \xrightarrow{\eta_{A}} G(F(A)) \xrightarrow{G(f)} G(B)`}
        </MathInline>
        . Going back: given <MathInline>{tex`g : A \to G B`}</MathInline>, the transpose{" "}
        <MathInline>{tex`\tilde{g} : F A \to B`}</MathInline> is{" "}
        <MathInline ariaLabel="F(A) maps via F(g) to F(G(B)) and then via ε_B to B">
          {tex`F(A) \xrightarrow{F(g)} F(G(B)) \xrightarrow{\varepsilon_{B}} B`}
        </MathInline>
        . The round trips <MathInline>{tex`\tilde{\tilde{f}} = f`}</MathInline> and{" "}
        <MathInline>{tex`\tilde{\tilde{g}} = g`}</MathInline> are exactly the two triangle
        identities. This is why the triangle identities are not a separate axiom — they are the
        statement that <MathInline>{tex`\eta`}</MathInline> and{" "}
        <MathInline>{tex`\varepsilon`}</MathInline> are inverse transpositions.
      </p>
      <p>
        <strong>Why both presentations matter.</strong> The hom-isomorphism shows that an adjunction
        is a <em>structural</em> trade: not an extra layer of bookkeeping, but a rebrand of the same
        morphism. The unit/counit + triangle identity presentation shows that the trade is{" "}
        <em>witnessed</em> by concrete maps — and those maps are what a proof assistant manipulates.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        Every CSK <strong>Dual Pair Guarantee</strong> is an adjunction. They are not three
        unrelated safety stories — they are three instances of the same algebraic record:
      </p>
      <ul>
        <li>
          <code>DATA.LINEAGE</code> (D1 + D2): fibration ⊣ opfibration, i.e. cartesian reindex ⊣
          opcartesian pushforward. Knowing where data came from and where it can reach are two sides
          of one bijection.
        </li>
        <li>
          <code>INV.CONSISTENCY</code> (I1 + I2): free F-algebra ⊣ cofree F-coalgebra. Establishing
          an invariant and preserving it across every observable step are two sides of one
          bijection.
        </li>
        <li>
          <code>RES.BALANCE</code> (R1 + R2):{" "}
          <MathInline ariaLabel="(− tensor A) is left adjoint to (A-star tensor −)">
            {tex`(- \otimes A) \dashv (A^{*} \otimes -)`}
          </MathInline>
          . Consuming a resource and releasing it are two sides of one bijection.
        </li>
      </ul>
      <p>
        Every CSK <strong>Structural Duality Principle</strong> is an adjunction at the
        2-categorical level — see the <em>higher/graded adjunctions</em> card for the regime where
        the seam is not a binary "exists/does-not-exist" but a graded "exists at grade{" "}
        <MathInline>{tex`k`}</MathInline>".
      </p>
      <p>
        And finally — this is where adjunctions earn their place in a security framework —{" "}
        <strong>Beck-Chevalley failure</strong> is precisely the phenomenon CSK exploits to make the
        unauthorized crossing of a seam structurally inexpressible. A pullback square that carries
        adjunctions on its edges has a canonical "mate" 2-cell; when that mate is an isomorphism the
        square has a bridge; when it is not, the square is a seam. Read the <em>Beck-Chevalley</em>{" "}
        card next.
      </p>
      <p>
        <strong>Concrete safety payoff.</strong> Adjunctions are why "two safety stories compose
        into a single envelope" is a theorem and not a slogan. Once a Dual Pair is presented as an
        adjunction, every claim about a single side — drift, vacuity, missing audit — is recoverable
        mechanically as a missing unit or counit; and the Immunity Closure Guarantees package each
        adjunction's unit and counit as the two witnesses an auditor must produce.
      </p>
    </>
  ),
  references: [
    {
      label: "Monads-And-Algebras",
      path: "theory/type-theory/Monads-And-Algebras.md",
      note: "Adjunction definition, unit/counit, triangle identities, hom-isomorphism.",
    },
    {
      label: "Seam theory",
      path: "theory/structural/SEAM-THEORY.md",
      note: "Beck-Chevalley failure as the structural seam, bridges as restricted Kan data.",
    },
    {
      label: "Pullback / Pushout / BC / Adjunction framework",
      path: "theory/structural/Pullback-Pushout-BC-Adjunction-Framework.md",
      note: "Single coherent story linking seams, BC failure, and Kan extensions.",
    },
    {
      label: "Adjunction composition (Agda)",
      path: "agda/Foundation/BeckChevalley/CategoriesLib/AdjunctionComposition.agda",
    },
    {
      label: "MonadFromAdjunction (MathLibCore, Agda)",
      path: "agda/MathLibCore/CategoryTheory/MonadFromAdjunction.agda",
      note: "Every adjunction induces a monad T = G ∘ F with μ = G ε F.",
    },
    {
      label: "Structural Invariant Safety Guarantees manuscript (PR #1544)",
      path: "https://github.com/InvariantHoldings/categorical-safety-kernel/pull/1544",
      note: "Chapter 05 explains F-algebra ⊣ F-coalgebra as the ALWAYS adjunction.",
    },
  ],
};

export function AdjunctionExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<AdjunctionDiagram accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
